import React, { useCallback, useMemo } from 'react';
import SearchableSelect, { type SelectOption } from '../SearchableSelect/SearchableSelect';
import { AppTypography } from '../appUi';
import AppFieldLine from './AppFieldLine';
import AppReadonlyField from './AppReadonlyField';
import AppInputLabel from './AppInputLabel';
import styles from './AppFormSearchableSelect.module.css';

interface AppFormSearchableSelectProps {
  id?: string;
  name?: string;
  label?: string;
  labelAction?: React.ReactNode;
  error?: string;
  required?: boolean;
  value: string;
  options: readonly SelectOption[] | SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
  disabled?: boolean;
  loading?: boolean;
  readOnly?: boolean;
  /** When false, hides the empty "Nenhuma opção" choice. Defaults to true. */
  allowEmpty?: boolean;
  containerClassName?: string;
  triggerFormat?: (option: SelectOption) => React.ReactNode;
  optionFormat?: (option: SelectOption) => React.ReactNode;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

const AppFormSearchableSelect: React.FC<AppFormSearchableSelectProps> = ({
  label,
  labelAction,
  error,
  required = false,
  options,
  placeholder = 'Selecione...',
  searchPlaceholder = 'Pesquisar',
  noResultsText = 'Nenhum resultado encontrado',
  id,
  name,
  containerClassName = '',
  readOnly = false,
  allowEmpty = true,
  triggerFormat,
  optionFormat,
  onChange,
  disabled,
  loading,
  value,
}) => {
  const searchableOptions = useMemo(
    () => options.map((option) => ({ ...option })),
    [options],
  );

  const triggerClassName = [
    styles.trigger,
    readOnly ? styles.triggerReadOnly : '',
  ].filter(Boolean).join(' ');

  const handleValueChange = useCallback((nextValue: string) => {
    if (!onChange) {
      return;
    }

    const syntheticEvent = {
      target: {
        value: nextValue,
        name: name ?? '',
        id: id ?? '',
      },
      currentTarget: {
        value: nextValue,
        name: name ?? '',
        id: id ?? '',
      },
    } as React.ChangeEvent<HTMLSelectElement>;

    onChange(syntheticEvent);
  }, [id, name, onChange]);

  const showRequired = required && !readOnly;
  const selected = options.find((option) => option.value === value);
  const readonlyValue = selected
    ? (triggerFormat ? triggerFormat(selected) : selected.label)
    : '';

  if (readOnly) {
    return (
      <AppReadonlyField
        id={id}
        label={label}
        value={readonlyValue}
        containerClassName={containerClassName}
      />
    );
  }

  return (
    <div id={id} className={`${styles.container} ${containerClassName}`}>
      {label && (
        <div className={styles.labelRow}>
          <AppInputLabel htmlFor={id} required={showRequired}>{label}</AppInputLabel>
          {labelAction}
        </div>
      )}
      <div className={styles.searchableSelectWrapper}>
        <SearchableSelect
          value={value}
          onChange={handleValueChange}
          options={searchableOptions}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          noResultsText={noResultsText}
          disabled={disabled}
          loading={loading}
          hasError={Boolean(error)}
          readOnly={readOnly}
          allowEmpty={allowEmpty}
          triggerFormat={triggerFormat}
          optionFormat={optionFormat}
          className={triggerClassName}
        />
      </div>
      <AppFieldLine error={Boolean(error)} />
      {error ? (
        <AppTypography variant="caption" className={styles.errorText}>
          {error}
        </AppTypography>
      ) : null}
    </div>
  );
};

export default AppFormSearchableSelect;
